// ─────────────────────────────────────────────────────────────────
// Glass-i — Fixed Assets API client (Stage 6B.9D).
//
// Thin axios wrappers around /api/erp/accounts/fixed-assets/*. The
// Approval-MFA challenge id flows through the standard
// `x-approval-mfa-challenge-id` header on delete / depreciation-post /
// disposal.
// ─────────────────────────────────────────────────────────────────

import client from '../../../../api/client';
import { translateQuery } from './fixedAssetsHelpers';

const BASE = '/api/erp/accounts';
const FA   = `${BASE}/fixed-assets`;
const CAT  = `${BASE}/fixed-assets/categories`;

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

function mfaHeaders(challengeId) {
  return challengeId ? { 'x-approval-mfa-challenge-id': challengeId } : undefined;
}

export const fixedAssetsApi = {
  // ── Categories ──────────────────────────────────────────────
  listCategories: (query) =>
    client.post(`${CAT}/list`, translateQuery(query) || {}).then(unwrap),
  getCategory: (id) =>
    client.get(`${CAT}/${encodeURIComponent(id)}`).then(unwrap),
  createCategory: (payload) =>
    client.post(CAT, payload).then(unwrap),
  updateCategory: (id, payload) =>
    client.put(`${CAT}/${encodeURIComponent(id)}`, payload).then(unwrap),
  deleteCategory: (id) =>
    client.delete(`${CAT}/${encodeURIComponent(id)}`).then(unwrap),

  // ── Fixed Assets ────────────────────────────────────────────
  list: (query) =>
    client.post(`${FA}/list`, translateQuery(query) || {}).then(unwrap),
  get: (id) =>
    client.get(`${FA}/${encodeURIComponent(id)}`).then(unwrap),
  create: (payload) =>
    client.post(FA, payload).then(unwrap),
  update: (id, payload) =>
    client.put(`${FA}/${encodeURIComponent(id)}`, payload).then(unwrap),
  remove: (id, { approvalMfaChallengeId } = {}) =>
    client.delete(`${FA}/${encodeURIComponent(id)}`,
      { headers: mfaHeaders(approvalMfaChallengeId) }).then(unwrap),

  // ── Depreciation ────────────────────────────────────────────
  previewDepreciation: (id, body) =>
    client.post(`${FA}/${encodeURIComponent(id)}/depreciation/preview`,
      body || {}).then(unwrap),
  postDepreciation: (id, body, { approvalMfaChallengeId } = {}) =>
    client.post(`${FA}/${encodeURIComponent(id)}/depreciation/post`,
      body || {}, { headers: mfaHeaders(approvalMfaChallengeId) }).then(unwrap),

  // ── Disposal ────────────────────────────────────────────────
  dispose: (id, body, { approvalMfaChallengeId } = {}) =>
    client.post(`${FA}/${encodeURIComponent(id)}/dispose`,
      body || {}, { headers: mfaHeaders(approvalMfaChallengeId) }).then(unwrap),

  // ── Ledger lookups (re-uses existing accounts endpoints) ────
  ledgerLookup: (q, limit = 50) =>
    client.get(`${BASE}/ledgers/lookup`, {
      params: { ...(q ? { q } : {}), limit }
    }).then(unwrap),
  ledgersBySubtype: (subtype, { q, limit = 100 } = {}) =>
    client.get(`${BASE}/ledgers/lookups/by-subtype`, {
      params: { subtype, ...(q ? { q } : {}), limit }
    }).then(unwrap)
};

export default fixedAssetsApi;
